<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import { ApiError } from '@/composables/api'
import PasswordInput from '@/components/password.vue'

const { t } = useI18n()
const MIN_LENGTH = 12
const props = defineProps({
  username: { type: String, required: true },
  prid: { type: String, required: false, default: '' },
  fio: { type: String, required: false, default: '' },
  service: { type: String, required: true },
})
const emit = defineEmits(['success', 'close'])

const { push: openModal } = useMessageStack()

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const responseModel = ref(null)

const CATEGORIES = {
  upper: { test: /\p{Lu}/u },
  lower: { test: /\p{Ll}/u },
  digit: { test: /[0-9]/ },
  special: { test: /[`~!@#$%^&*()_\-+=[\]{}|;:"'<>?,./\\ ]/ },
}

function countCategories(pwd) {
  if (!pwd) return 0
  return Object.values(CATEGORIES).filter(({ test }) => test.test(pwd)).length
}

function normalizeStr(s) {
  return String(s || '').toLowerCase().trim()
}

function containsToken(password, token) {
  const p = normalizeStr(password)
  const t = normalizeStr(token)
  if (!p || !t) return false
  if (t.length < 3) return false
  return p.includes(t)
}

const newPasswordHint = computed(() => {
  const p = newPassword.value || ''
  if (p.length === 0) return ''
  if (p.length < MIN_LENGTH) return t('hints.minLength', { n: MIN_LENGTH })
  if (countCategories(p) < 3) {
    return t('hints.categories', {
      categories: Object.keys(CATEGORIES).map(category => t(`categories.${category}`)).join(', '),
    })
  }
  const un = (props.username || '').toLowerCase()
  if (un && p.toLowerCase().includes(un)) return t('hints.noLogin')
  const prid = props.prid || ''
  if (containsToken(p, prid)) return t('hints.noPrid')
  const fio = props.fio || ''
  const fioTokens = normalizeStr(fio).split(/\s+/).filter(Boolean)
  if (fioTokens.some(t => containsToken(p, t))) return t('hints.noName')
  return ''
})

const confirmHint = computed(() => {
  if (confirmPassword.value.length === 0) return ''
  if (newPassword.value !== confirmPassword.value) return t('hints.noMatch')
  return ''
})

const canSubmit = computed(() => {
  return currentPassword.value && newPassword.value && confirmPassword.value &&
    !newPasswordHint.value && !confirmHint.value
})

const { refresh: doChangePassword } = useApi({
  model: responseModel,
  method: 'POST',
  url: 'login/change_password',
})

async function submit() {
  if (!canSubmit.value || loading.value) return
  try {
    loading.value = true
    await doChangePassword({
      data: {
        username: props.username,
        current_password: currentPassword.value,
        new_password: newPassword.value,
        service: props.service,
      },
    })
    if (responseModel.value?.require_2fa) {
      emit('success', {
        require_2fa: true,
        email: responseModel.value?.email_notifications || responseModel.value?.email,
        totp_enabled: !!responseModel.value?.totp_enabled,
      })
      return
    }
    // Не записываем токен — переходим на страницу логина, пользователь входит новым паролем
    emit('success')
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : t('errors.generic')
    openModal({ type: 'error', message: msg })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="change-pass">
    <p class="text-muted mb-3">{{ t('intro') }}</p>
    <form @submit.prevent="submit">
      <div class="mb-3">
        <label class="form-label">{{ t('labels.currentPassword') }}</label>
        <PasswordInput v-model="currentPassword" autocomplete="current-password" />
      </div>
      <div class="mb-3">
        <label class="form-label">{{ t('labels.newPassword') }}</label>
        <PasswordInput v-model="newPassword" />
        <small v-if="newPasswordHint" class="text-danger d-block mt-1">{{ newPasswordHint }}</small>
      </div>
      <div class="mb-3">
        <label class="form-label">{{ t('labels.confirmPassword') }}</label>
        <PasswordInput v-model="confirmPassword" />
        <small v-if="confirmHint" class="text-danger d-block mt-1">{{ confirmHint }}</small>
      </div>
      <div class="d-flex gap-2 justify-content-end">
        <button type="button" class="btn btn-outline-secondary" @click="emit('close')">
          {{ t('common.cancel') }}
        </button>
        <button type="submit" class="btn btn-primary" :disabled="!canSubmit || loading">
          {{ loading ? t('actions.saving') : t('actions.submit') }}
        </button>
      </div>
    </form>
  </div>
</template>

<i18n>
{
  "ru": {
    "intro": "Пароль устарел. Необходимо сменить пароль.",
    "labels": {
      "currentPassword": "Текущий пароль",
      "newPassword": "Новый пароль",
      "confirmPassword": "Подтверждение пароля"
    },
    "actions": {
      "saving": "Сохранение...",
      "submit": "Сменить пароль"
    },
    "categories": {
      "upper": "заглавные буквы",
      "lower": "строчные буквы",
      "digit": "цифры",
      "special": "спецсимволы"
    },
    "hints": {
      "minLength": "Пароль должен быть не менее {n} символов",
      "categories": "Пароль должен содержать символы минимум из трёх категорий: {categories}",
      "noLogin": "Пароль не должен содержать логин",
      "noPrid": "Пароль не должен содержать имя пользователя (PRID)",
      "noName": "Пароль не должен содержать настоящее имя",
      "noMatch": "Пароль и подтверждение не совпадают"
    },
    "errors": {
      "generic": "Ошибка при смене пароля"
    }
  },
  "en": {
    "intro": "Your password has expired. Please change it.",
    "labels": {
      "currentPassword": "Current password",
      "newPassword": "New password",
      "confirmPassword": "Confirm password"
    },
    "actions": {
      "saving": "Saving...",
      "submit": "Change password"
    },
    "categories": {
      "upper": "uppercase letters",
      "lower": "lowercase letters",
      "digit": "digits",
      "special": "special characters"
    },
    "hints": {
      "minLength": "Password must be at least {n} characters",
      "categories": "Password must include characters from at least three categories: {categories}",
      "noLogin": "Password must not contain your login",
      "noPrid": "Password must not contain PRID",
      "noName": "Password must not contain your real name",
      "noMatch": "Password and confirmation do not match"
    },
    "errors": {
      "generic": "Failed to change password"
    }
  }
}
</i18n>
